import {
    faCircleExclamation,
    faMultiply,
    faTriangleExclamation,
    IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from "react";
import { Dropdown, Offcanvas } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
    APP_IDS,
    EMPTY_USERFUL_SECURITY_DATA,
    StringID,
    ClearanceLevelID,
} from "userful-chronos-app-common-js/dist/models/common";
import { CompGroup } from "userful-chronos-app-common-js/dist/models/group/Groups";
import {
    requestCreateGroup,
    requestUpdateGroup,
} from "userful-chronos-app-common-js/dist/models/group/messages/GroupMsgSender";
import { generateUUID } from "userful-chronos-app-common-js/dist/utils";
import Button from "../../button/Button";
import UserfulConfirmationDialog from "../../ConfirmationDialog/UserfulConfirmationDialog";
import Input from "../../Input/Input";
import Textarea from "../../Textfield/Textarea";
import UserfulTooltip from "../../Widgets/UserfulTooltip";
import { SourcePermissionsForm } from "../../sourceassets";
import { deepCopy, objectEqual } from "./objectComparison";
import { generatedUniqueName, recursivellyUpdateAllPermissionsWithinFolder } from "./utils";

import { AppsConfig } from "../../SAM/formUtils";
import "./UserfulForm.scss";

interface IProps {
    type: "SECONDARY" | "DROPDOWN";
    comp: CompGroup | null | undefined;
    parent?: CompGroup;
    comps: CompGroup[];
    onCreate?: (folderID: StringID) => void;
    hideTrigger?: boolean;
}

export type CreateFolderHandle = {
    open: () => void;
    close: () => void;
};

const initCreateGroup = (): CompGroup => {
    return {
        id: { value: generateUUID() },
        name: "",
        description: "",
        type: "SOURCE_FOLDER",
        color: "#000000",
        appInstanceID: { value: generateUUID(), appID: { value: APP_IDS.Config } },
        comps: [],
        createTime: Date.now(),
        updateTime: Date.now(),
        userfulSecurityData: EMPTY_USERFUL_SECURITY_DATA(),
        appIDs: {
            appIDs: AppsConfig.map((app) => ({ value: app.id })),
            valid: true,
        },
        tagsSet: {
            tags: [],
            valid: true,
        },
        folderPath: {
            elements: [],
            valid: false,
        },
        parentGroupID: { value: "NOT_SET" },
    };
};

export type TFolderNavigations = "SETTINGS" | "PERMISSIONS";

interface IActionsPanelNavigationItem {
    id: string;
    translationString: string;
    disabled?: boolean;
    icon?: IconDefinition;
    onClick: () => void;
}

const getSortedPermissionValues = (folder?: CompGroup | null): string[] => {
    const permissions = folder?.userfulSecurityData?.clearanceLevelPermissions ?? [];
    return permissions
        .map((permission) => permission.value)
        .filter((value): value is string => typeof value === "string")
        .sort();
};

const CreateFolder = forwardRef<CreateFolderHandle, IProps>(function CreateFolder(props, ref) {
    const { t } = useTranslation();

    const [showForm, setShowForm] = useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [showPermissionsWarning, setShowPermissionsWarning] = useState<boolean>(false);
    const [actionError, setActionError] = useState<string | null>(null);

    const [currentNavigation, setCurrentNavigation] = useState<TFolderNavigations>("SETTINGS");
    const [navigationItems, setNavigationItems] = useState<IActionsPanelNavigationItem[]>([]);

    const safeComps = props.comps || [];

    const [originalFolder, setOriginalFolder] = useState<CompGroup>(props.comp ? props.comp : initCreateGroup());
    const [currentFolder, setCurrentFolder] = useState<CompGroup>(deepCopy(originalFolder));

    // ——— Helpers for validation ———
    const trimmedName = (currentFolder.name ?? "").trim();
    const isNameEmpty = trimmedName.length === 0;

    // Case-insensitive duplicate check; exclude current when editing
    const isNameDuplicate = safeComps.some((c) => {
        const sameId = props.comp ? c?.id?.value === props.comp?.id?.value : false;
        if (sameId) return false;
        return (c?.name ?? "").trim().toLowerCase() === trimmedName.toLowerCase();
    });

    const isNameError = isNameEmpty || isNameDuplicate;

    const foundChanges = !objectEqual(originalFolder, currentFolder);
    const namePlaceholder = generatedUniqueName([], "Untitled", "folder"); // still used as UI placeholder only

    const permissionsChanged = useMemo(() => {
        if (!props.comp) {
            return false;
        }
        const originalPermissions = getSortedPermissionValues(originalFolder);
        const currentPermissions = getSortedPermissionValues(currentFolder);
        if (originalPermissions.length !== currentPermissions.length) {
            return true;
        }
        return originalPermissions.some((value, index) => value !== currentPermissions[index]);
    }, [currentFolder, originalFolder, props.comp]);

    const parentRequiredPermissions = useMemo<ClearanceLevelID[]>(() => {
        const permissions = props.parent?.userfulSecurityData?.clearanceLevelPermissions ?? [];
        return permissions.map((permission) => ({ ...permission }));
    }, [props.parent]);

    const enforceParentPermissions = useCallback(
        (folder: CompGroup): CompGroup => {
            if (!parentRequiredPermissions.length) {
                return folder;
            }

            const securityData = folder.userfulSecurityData ?? EMPTY_USERFUL_SECURITY_DATA();
            const updatedPermissions = Array.isArray(securityData.clearanceLevelPermissions)
                ? [...securityData.clearanceLevelPermissions]
                : [];

            let changed = false;
            parentRequiredPermissions.forEach((permission) => {
                if (!updatedPermissions.some((existing) => existing.value === permission.value)) {
                    updatedPermissions.push({ value: permission.value });
                    changed = true;
                }
            });

            if (!changed) {
                return folder;
            }

            return {
                ...folder,
                userfulSecurityData: {
                    ...securityData,
                    clearanceLevelPermissions: updatedPermissions,
                },
            };
        },
        [parentRequiredPermissions],
    );

    // Enable/disable primary button:
    // - Create: only if name valid (non-empty, not duplicate)
    // - Edit: only if something changed AND name valid
    const canSave = props.comp ? foundChanges && !isNameError : !isNameError;

    const onChangePermission = (checkBool: boolean, value: string) => {
        setCurrentFolder((prevFolder) => {
            const securityData = prevFolder.userfulSecurityData ?? EMPTY_USERFUL_SECURITY_DATA();
            const updatedPermissions = Array.isArray(securityData.clearanceLevelPermissions)
                ? [...securityData.clearanceLevelPermissions]
                : [];

            if (checkBool) {
                if (!updatedPermissions.some((item) => item.value === value)) {
                    updatedPermissions.push({ value });
                }
            } else {
                const indexToRemove = updatedPermissions.findIndex((item) => item.value === value);
                if (indexToRemove !== -1) {
                    updatedPermissions.splice(indexToRemove, 1);
                }
            }

            return {
                ...prevFolder,
                userfulSecurityData: {
                    ...securityData,
                    clearanceLevelPermissions: updatedPermissions,
                },
            };
        });
    };

    const onFormShow = () => {
        // sync originals at open time in case props changed
        const base = props.comp ? deepCopy(props.comp) : initCreateGroup();
        const enforcedBase = enforceParentPermissions(base);
        setOriginalFolder(enforcedBase);
        setCurrentFolder(deepCopy(enforcedBase));
        setShowForm(true);
        setActionError(null);
    };

    const onFormHide = () => {
        if (foundChanges) {
            setShowConfirmation(true);
        } else {
            onRestore();
            setShowForm(false);
        }
    };

    const onRestore = () => {
        const base = props.comp ? deepCopy(props.comp) : initCreateGroup();
        const enforcedBase = enforceParentPermissions(base);

        setCurrentNavigation("SETTINGS");
        setOriginalFolder(enforcedBase);
        setCurrentFolder(deepCopy(enforcedBase));
    };

    const handleCreate = async () => {
        if (isNameError) {
            return;
        }
        setActionError(null);
        try {
            props.onCreate?.(currentFolder.id);
            await Promise.resolve(
                requestCreateGroup({
                    ...currentFolder,
                    name: trimmedName,
                }),
            );
            onRestore();
            setShowForm(false);
        } catch (err) {
            console.error("Failed to create folder", err);
            setActionError(t("Folder.Form.saveError", { defaultValue: "Unable to save folder. Please try again." }));
        }
    };

    const handleUpdate = async (shouldCascade: boolean) => {
        if (isNameError) {
            return;
        }
        setActionError(null);
        try {
            await Promise.resolve(
                requestUpdateGroup({
                    ...currentFolder,
                    name: trimmedName,
                }),
            );

            if (shouldCascade) {
                await Promise.resolve(recursivellyUpdateAllPermissionsWithinFolder(currentFolder));
            }

            onRestore();
            setShowForm(false);
        } catch (err) {
            console.error("Failed to update folder", err);
            setActionError(t("Folder.Form.saveError", { defaultValue: "Unable to save folder. Please try again." }));
        }
    };

    const onCreateOrUpdate = async () => {
        // Final guard (should already be disabled via canSave)
        if (isNameError) return;

        if (!props.comp) {
            // CREATE
            await handleCreate();
        } else {
            // UPDATE
            if (permissionsChanged) {
                setShowPermissionsWarning(true);
                return;
            }
            await handleUpdate(false);
        }
    };

    const handlePermissionWarningConfirm = () => {
        setShowPermissionsWarning(false);
        handleUpdate(true);
    };

    const handlePermissionWarningCancel = () => {
        setShowPermissionsWarning(false);
    };

    useEffect(() => {
        setNavigationItems([
            {
                id: "SETTINGS",
                translationString: "Shared.settings",
                onClick: () => setCurrentNavigation("SETTINGS"),
            },
            {
                id: "PERMISSIONS",
                translationString: "Shared.permissions",
                onClick: () => setCurrentNavigation("PERMISSIONS"),
            },
        ]);
        // If props.comp changes while component is mounted, keep states in sync
    }, []);

    useEffect(() => {
        const base = props.comp ? deepCopy(props.comp) : initCreateGroup();
        const enforcedBase = enforceParentPermissions(base);
        setOriginalFolder(enforcedBase);
        setCurrentFolder(deepCopy(enforcedBase));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.comp, enforceParentPermissions]);

    useEffect(() => {
        setCurrentFolder((folder) => enforceParentPermissions(folder));
        setOriginalFolder((folder) => enforceParentPermissions(folder));
    }, [parentRequiredPermissions, enforceParentPermissions]);

    useImperativeHandle(ref, () => ({
        open: () => onFormShow(),
        close: () => onFormHide(),
    }));

    return (
        <>
            {!props.hideTrigger && props.type === "SECONDARY" && (
                <Button variant="secondary" onClick={(e) => onFormShow()}>
                    {props.comp ? t("Folder.Form.edit") : t("Folder.Form.create")}
                </Button>
            )}

            {!props.hideTrigger && props.type === "DROPDOWN" && (
                <Dropdown.Item onClick={(e) => onFormShow()}>
                    <p>{props.comp ? t("Shared.edit") : t("Shared.create")}</p>
                </Dropdown.Item>
            )}

            <Offcanvas show={showForm} onHide={onFormHide} placement="end">
                <div className="userful-exclusive-form">
                    <div className="userful-exclusive-form-header">
                        <div className="userful-exclusive-form-main-header">
                            <div className="userful-exclusive-form-header-text">
                                <h1>{props.comp ? t("Folder.Form.editThis") : t("Folder.Form.create")}</h1>
                                <p>{t("Folder.Form.description")}</p>
                            </div>
                            <UserfulTooltip title={t("Shared.close")}>
                                <Button iconOnly variant="secondary" onClick={onFormHide}>
                                    <FontAwesomeIcon icon={faMultiply} />
                                </Button>
                            </UserfulTooltip>
                        </div>
                        <div className="multi-state-toggle-wrapper">
                            {navigationItems.map((item) => {
                                return (
                                    <button
                                        disabled={item.disabled}
                                        key={item.id}
                                        className={`multi-state-toggle ${currentNavigation === item.id ? "multi-state-toggle-active" : ""}`}
                                        onClick={item.onClick}
                                    >
                                        <p>{t(item.translationString)}</p>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="userful-exclusive-form-body">
                        {currentNavigation === "SETTINGS" && (
                            <div className="userful-exclusive-form-block-wrapper">
                                <div className="userful-exclusive-form-text-block-wrapper">
                                    <h1>{t("Folder.Form.details")}</h1>
                                    <p>{t("Folder.Form.detailsDescription")}</p>
                                </div>
                                <div className="userful-exclusive-form-block-wrapper">
                                    <Input
                                        onChange={(e) => {
                                            e.preventDefault();
                                            setCurrentFolder({ ...currentFolder, name: e.target.value });
                                        }}
                                        help={t("Decisions.Form.nameHelp")}
                                        filedTitle={t("Shared.name")}
                                        value={currentFolder.name}
                                        maxLength={60}
                                        placeholder={t("Shared.namePlaceholder") || namePlaceholder}
                                        preChildren={
                                            <p className="userful-exclusive-form-count">
                                                {currentFolder.name.length}/60
                                            </p>
                                        }
                                        error={foundChanges && isNameError}
                                        postChildren={
                                            foundChanges &&
                                            isNameError && (
                                                <p className="userful-exclusive-form-error">
                                                    {isNameEmpty
                                                        ? t("Folder.Form.nameRequired") // ensure you have this key; fallback to nameError if you prefer
                                                        : t("Folder.Form.nameError")}
                                                </p>
                                            )
                                        }
                                    />

                                    <Textarea
                                        onChange={(e) => {
                                            e.preventDefault();
                                            setCurrentFolder({ ...currentFolder, description: e.target.value });
                                        }}
                                        filedTitle={t("Shared.description")}
                                        value={currentFolder.description}
                                        maxLength={200}
                                        placeholder=""
                                        preChildren={
                                            <p className="userful-exclusive-form-count">
                                                {currentFolder.description.length}/200
                                            </p>
                                        }
                                    />
                                </div>
                            </div>
                        )}

                        {currentNavigation === "PERMISSIONS" && (
                            <div className="userful-exclusive-form-block-wrapper">
                                <SourcePermissionsForm
                                    noMarginTop
                                    data={currentFolder.userfulSecurityData?.clearanceLevelPermissions || []}
                                    onChange={(checkBool, value) => onChangePermission(checkBool, value)}
                                    mustHave={parentRequiredPermissions}
                                    isSourceOrDestination
                                />
                            </div>
                        )}
                    </div>

                    <div className="userful-exclusive-form-footer">
                        <Button variant="secondary" onClick={onFormHide}>
                            {t("Shared.cancel")}
                        </Button>
                        <Button variant="primary" disabled={!canSave} onClick={onCreateOrUpdate}>
                            {props.comp ? t("Shared.saveChanges") : t("Shared.create")}
                        </Button>
                        {actionError && (
                            <p className="userful-exclusive-form-error" style={{ marginLeft: "16px" }}>
                                {actionError}
                            </p>
                        )}
                    </div>
                </div>
            </Offcanvas>

            <UserfulConfirmationDialog
                show={showPermissionsWarning}
                varient="warning"
                title={t("Folder.Form.permissionsChangeWarningTitle", { defaultValue: "Update nested permissions?" })}
                replaceBody={
                    <p>
                        {t("Folder.Form.permissionsChangeWarningDescription", {
                            defaultValue:
                                "Updating this folder's permissions will also update all nested folders and assets. Do you want to continue?",
                        })}
                    </p>
                }
                onCancel={handlePermissionWarningCancel}
                onConfirm={handlePermissionWarningConfirm}
                icon={{ icon: faCircleExclamation }}
            />

            <UserfulConfirmationDialog
                show={showConfirmation}
                varient="danger"
                title={t("Notifications.Form.leave")}
                replaceBody={<p>{t("Notifications.Form.leaveDescription")}</p>}
                onCancel={() => {
                    setShowConfirmation(false);
                }}
                onConfirm={() => {
                    setShowForm(false);
                    setShowConfirmation(false);
                    onRestore();
                }}
                icon={{ icon: faTriangleExclamation, color: "#FFA200" }}
            />
        </>
    );
});

export default CreateFolder;
